import { Home, HardHat, Sparkles } from "lucide-react";
import { useAuth } from "@contexts/AuthContext";
import { useState } from "react";

/**
 * First-run modal shown when the signed-in user has no userType yet.
 * Two options: Homeowner (plans their own renovation) or Contractor
 * (runs a firm with multiple client projects).
 *
 * Phase 2: picker only sets userType. Homeowner onboarding wizard lands
 * in Step 6; contractor org creation lands in Phase 3.
 */
export default function PersonaPicker() {
  const { setUserType, user } = useAuth();
  const [submitting, setSubmitting] = useState(null);

  const pick = async (userType) => {
    if (!user || submitting) return;
    setSubmitting(userType);
    try {
      await setUserType(userType);
    } catch (err) {
      console.error("[PersonaPicker] setUserType failed", err);
      setSubmitting(null);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{
        zIndex: 2147483645,
        background: "rgba(0, 0, 0, 0.72)",
        backdropFilter: "blur(8px)",
      }}
    >
      <div
        className="rounded-xl w-full max-w-2xl p-6"
        style={{
          background: "var(--bg-root)",
          border: "1px solid var(--border-card)",
          boxShadow:
            "0 24px 60px rgba(0,0,0,0.55), 0 6px 20px rgba(0,0,0,0.35)",
        }}
      >
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4" style={{ color: "var(--accent-text)" }} />
          <p
            className="text-[10px] uppercase tracking-wider font-semibold"
            style={{ color: "var(--text-secondary)" }}
          >
            Welcome
          </p>
        </div>
        <h2
          className="text-xl font-bold mb-1"
          style={{ color: "var(--text-heading)" }}
        >
          How do you plan to use Posh Block?
        </h2>
        <p
          className="text-sm leading-relaxed mb-6"
          style={{ color: "var(--text-body)" }}
        >
          We'll tailor the experience based on how you answer. You can change
          this later in Settings.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PersonaCard
            icon={<Home className="w-6 h-6" />}
            title="Homeowner"
            description="Plan my own renovation. Invite designers, contractors, and family to help."
            onClick={() => pick("homeowner")}
            loading={submitting === "homeowner"}
            accent="#10b981"
          />
          <PersonaCard
            icon={<HardHat className="w-6 h-6" />}
            title="Contractor / Designer"
            description="Run a firm with multiple client projects. Invite teammates and clients per project."
            onClick={() => pick("contractor")}
            loading={submitting === "contractor"}
            accent="#6366f1"
          />
        </div>
      </div>
    </div>
  );
}

function PersonaCard({ icon, title, description, onClick, loading, accent }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="text-left p-4 rounded-lg transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-wait"
      style={{
        background: "var(--bg-card)",
        border: `1px solid ${accent}66`,
      }}
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
        style={{ background: `${accent}1f`, color: accent }}
      >
        {icon}
      </div>
      <p
        className="text-sm font-semibold mb-1"
        style={{ color: "var(--text-heading)" }}
      >
        {title}
      </p>
      <p
        className="text-xs leading-relaxed"
        style={{ color: "var(--text-body)" }}
      >
        {description}
      </p>
      {loading && (
        <p className="text-[10px] mt-2" style={{ color: accent }}>
          Saving…
        </p>
      )}
    </button>
  );
}
